import type { ShopLanguage, ShopMessageKey } from '../../../i18n'
import type { ActiveFilterChip } from './ordersListSectionTypes'
import {
  getStatusSortOrder,
  joinChipParts,
  toVisualStatusLabel,
  type OrderDateFilterOption,
  type OrderVisualStatus,
} from './ordersListShared'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type BuildActiveOrderFilterChipsInput = {
  dateFilter: OrderDateFilterOption
  customDateFrom: string
  customDateTo: string
  dateFormatter: Intl.DateTimeFormat
  statusFilters: OrderVisualStatus[]
  language: ShopLanguage
  priceMin: string
  priceMax: string
  t: TranslateFn
  onResetDateFilter: () => void
  onRemoveStatusFilter: (status: OrderVisualStatus) => void
  onResetPriceRange: () => void
}

export const buildActiveOrderFilterChips = ({
  dateFilter,
  customDateFrom,
  customDateTo,
  dateFormatter,
  statusFilters,
  language,
  priceMin,
  priceMax,
  t,
  onResetDateFilter,
  onRemoveStatusFilter,
  onResetPriceRange,
}: BuildActiveOrderFilterChipsInput): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = []

  if (dateFilter !== 'all') {
    const dateFilterLabel =
      dateFilter === 'last-30-days'
        ? t('customer.orders.filters.date.last30')
        : dateFilter === 'last-3-months'
          ? t('customer.orders.filters.date.last3Months')
          : dateFilter === 'last-year'
            ? t('customer.orders.filters.date.lastYear')
            : dateFilter === 'custom'
              ? t('customer.orders.filters.date.custom')
              : t('customer.orders.filters.date.all')

    const customRangeLabel =
      dateFilter === 'custom'
        ? joinChipParts(
            customDateFrom ? dateFormatter.format(new Date(`${customDateFrom}T00:00:00`)) : null,
            customDateTo ? dateFormatter.format(new Date(`${customDateTo}T00:00:00`)) : null,
          )
        : null

    chips.push({
      id: 'date-filter',
      label:
        dateFilter === 'custom' && customRangeLabel
          ? `${t('customer.orders.filters.date.custom')}: ${customRangeLabel}`
          : dateFilterLabel,
      onRemove: onResetDateFilter,
    })
  }

  statusFilters
    .slice()
    .sort((left, right) => getStatusSortOrder(left) - getStatusSortOrder(right))
    .forEach((status) => {
      chips.push({
        id: `status-${status}`,
        label: toVisualStatusLabel(status, language),
        onRemove: () => {
          onRemoveStatusFilter(status)
        },
      })
    })

  if (priceMin.trim() || priceMax.trim()) {
    chips.push({
      id: 'price-range',
      label: `${priceMin.trim() || '0'} EUR - ${priceMax.trim() || '...'} EUR`,
      onRemove: onResetPriceRange,
    })
  }

  return chips
}
